import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight, Sparkles } from 'lucide-react';

/*
 * "What You'll Gain at LayerSeek" — frosted-glass bento with bespoke visuals.
 * Copy is intentionally objective: no wild reach numbers, no fabricated dollar totals.
 */

// ─── Tile 1 · B2B Integrations — connector flow ───
const IntegrationsVisual = () => (
  <div className="relative h-32 sm:h-36 rounded-xl bg-[rgba(255,255,255,0.02)] border border-white/6 overflow-hidden">
    <svg viewBox="0 0 300 130" className="absolute inset-0 w-full h-full">
      <defs>
        <linearGradient id="int-line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.05" />
          <stop offset="50%" stopColor="var(--accent-primary)" stopOpacity="0.6" />
          <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path d="M 40 65 Q 90 20 150 65 T 260 65" stroke="url(#int-line)" strokeWidth="1.2" fill="none" strokeDasharray="3 3" />
      <path d="M 40 65 Q 90 110 150 65 T 260 65" stroke="url(#int-line)" strokeWidth="1.2" fill="none" strokeDasharray="3 3" />
      {[0.15, 0.45, 0.75].map((t, i) => (
        <circle key={i} r="2.5" fill="var(--accent-primary)">
          <animateMotion dur={`${3 + i * 0.4}s`} repeatCount="indefinite"
            path="M 40 65 Q 90 20 150 65 T 260 65"
            begin={`-${t * 3}s`} />
        </circle>
      ))}
    </svg>
    {[
      { l: 'Wallet', x: '8%' },
      { l: 'Bridge', x: '32%' },
      { l: 'Chain', x: '56%' },
      { l: 'Oracle', x: '80%' },
    ].map((c, i) => (
      <div
        key={c.l}
        className="absolute top-1/2 -translate-y-1/2 px-2 py-1 rounded-lg text-[10px] font-bold text-white/90"
        style={{
          left: c.x,
          background: 'rgba(20,22,28,0.85)',
          border: '1px solid rgba(218,255,1,0.35)',
          boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
          animation: `intPulse 3s ease-in-out ${i * 0.4}s infinite`,
        }}
      >
        {c.l}
      </div>
    ))}
    <style>{`
      @keyframes intPulse {
        0%, 100% { transform: translateY(-50%) scale(1); box-shadow: 0 8px 20px rgba(0,0,0,0.4); }
        50% { transform: translateY(-52%) scale(1.06); box-shadow: 0 12px 26px rgba(218,255,1,0.15); }
      }
    `}</style>
  </div>
);

// ─── Tile 2 · Co-Marketing — Spaces / AMAs ───
const CoMarketingVisual = () => (
  <div className="relative h-32 sm:h-36 rounded-xl bg-[rgba(255,255,255,0.02)] border border-white/6 overflow-hidden p-3">
    <div className="flex items-center gap-2 mb-2">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
      </span>
      <span className="text-[10px] font-bold text-white">LIVE · Space / AMA</span>
    </div>
    <div className="flex items-center gap-2 mb-3">
      {['#f472b6', '#38bdf8', '#4ade80', '#fbbf24', '#a78bfa'].map((c, i) => (
        <div
          key={i}
          className="w-8 h-8 rounded-full relative"
          style={{
            background: `linear-gradient(135deg, ${c}, ${c}88)`,
            border: `1.5px solid ${c}`,
            animation: `speakerRing 2.4s ease-in-out ${i * 0.3}s infinite`,
          }}
        />
      ))}
    </div>
    <div className="flex items-end gap-0.5 h-6">
      {[3, 8, 5, 12, 7, 15, 9, 18, 6, 14, 4, 11, 8, 16, 5, 10, 7, 13, 6, 9, 4, 12, 8, 15, 5].map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-t-sm"
          style={{
            height: `${h * 1.4}px`,
            background: 'linear-gradient(180deg, var(--accent-primary), rgba(218,255,1,0.3))',
            animation: `wave 1.2s ease-in-out ${i * 0.05}s infinite`,
          }}
        />
      ))}
    </div>
    <style>{`
      @keyframes speakerRing {
        0%, 100% { box-shadow: 0 0 0 0 currentColor; }
        50% { box-shadow: 0 0 0 4px rgba(218,255,1,0.15); }
      }
      @keyframes wave {
        0%, 100% { transform: scaleY(0.6); opacity: 0.7; }
        50% { transform: scaleY(1); opacity: 1; }
      }
    `}</style>
  </div>
);

// ─── Tile 3 · Ecosystem Grants — chain badges, no numbers ───
const GrantsVisual = () => (
  <div className="relative h-32 sm:h-36 rounded-xl bg-[rgba(255,255,255,0.02)] border border-white/6 overflow-hidden p-3">
    <div className="text-[9px] uppercase tracking-widest text-[var(--text-muted)] mb-2">Open programs across</div>
    <div className="grid grid-cols-3 gap-1.5 mb-2">
      {[
        { l: 'Arbitrum', c: '#2D374B' },
        { l: 'Base',     c: '#0052FF' },
        { l: 'Polygon',  c: '#8247E5' },
        { l: 'Solana',   c: '#14F195' },
        { l: 'ZK Sync',  c: '#8A72FF' },
        { l: 'Optimism', c: '#FF0420' },
      ].map((chain, i) => (
        <motion.div
          key={chain.l}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + i * 0.08 }}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/8"
          style={{ background: 'rgba(255,255,255,0.03)' }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: chain.c }} />
          <span className="text-[10px] font-semibold text-white/85 truncate">{chain.l}</span>
        </motion.div>
      ))}
    </div>
    <div className="flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)] animate-pulse" />
      <span className="text-[10px] font-medium text-[var(--accent-primary)]">Warm intros to program leads</span>
    </div>
  </div>
);

// ─── Tile 4 · Media & Distribution — real brand logos + PR outlets ───
// Inline SVG brand icons (crisp at any size, no CDN dependency)
const SocialIcon = ({ brand }) => {
  switch (brand) {
    case 'x':
      return (<svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>);
    case 'youtube':
      return (<svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12z"/></svg>);
    case 'tiktok':
      return (<svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>);
    case 'instagram':
      return (<svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M12 2.163c3.204 0 3.584.012 4.849.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.849.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>);
    case 'linkedin':
      return (<svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>);
    case 'telegram':
      return (<svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>);
    case 'discord':
      return (<svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>);
    default:
      return null;
  }
};

const MEDIA_ITEMS = [
  // Social
  { kind: 'icon', brand: 'x',         label: 'X',            bg: '#000000', color: '#FFFFFF' },
  { kind: 'icon', brand: 'youtube',   label: 'YouTube',      bg: '#FFFFFF', color: '#FF0000' },
  { kind: 'icon', brand: 'tiktok',    label: 'TikTok',       bg: '#000000', color: '#25F4EE' },
  { kind: 'icon', brand: 'instagram', label: 'Instagram',    bg: 'linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)', color: '#FFFFFF' },
  { kind: 'icon', brand: 'linkedin',  label: 'LinkedIn',     bg: '#0A66C2', color: '#FFFFFF' },
  { kind: 'icon', brand: 'telegram',  label: 'Telegram',     bg: '#26A5E4', color: '#FFFFFF' },
  { kind: 'icon', brand: 'discord',   label: 'Discord',      bg: '#5865F2', color: '#FFFFFF' },
  // PR / Media outlets — styled wordmarks
  { kind: 'word', word: 'CoinDesk',       label: 'CoinDesk',       bg: '#F8B003', color: '#000000', ff: 'serif',     size: '8.5px', weight: 900 },
  { kind: 'word', word: 'The Block',      label: 'The Block',      bg: '#FFFFFF', color: '#0F0F0F', ff: 'sans-serif',size: '8.5px', weight: 900 },
  { kind: 'word', word: 'CT',             label: 'Cointelegraph',  bg: '#F4B923', color: '#141414', ff: 'sans-serif',size: '13px',  weight: 900 },
  { kind: 'word', word: 'DECRYPT',        label: 'Decrypt',        bg: '#111111', color: '#00E599', ff: 'monospace', size: '7.5px', weight: 900 },
  { kind: 'word', word: 'DLNEWS',         label: 'DL News',        bg: '#0057FF', color: '#FFFFFF', ff: 'sans-serif',size: '7.5px', weight: 900 },
];

const MediaVisual = () => (
  <div className="relative h-32 sm:h-36 rounded-xl bg-[rgba(255,255,255,0.02)] border border-white/6 overflow-hidden p-2 sm:p-2.5">
    <div className="grid grid-cols-6 grid-rows-2 gap-1.5 sm:gap-2 h-full">
      {MEDIA_ITEMS.slice(0, 12).map((p, i) => (
        <motion.div
          key={p.label}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.04 + i * 0.04, duration: 0.4 }}
          title={p.label}
          className="flex items-center justify-center rounded-lg min-h-0"
          style={{
            background: p.bg,
            color: p.color,
            boxShadow: '0 4px 10px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.06)',
          }}
        >
          {p.kind === 'icon' ? (
            <div className="w-4 h-4 sm:w-[18px] sm:h-[18px]">
              <SocialIcon brand={p.brand} />
            </div>
          ) : (
            <span
              style={{
                fontFamily: p.ff,
                fontSize: p.size,
                fontWeight: p.weight,
                letterSpacing: p.word === 'DECRYPT' || p.word === 'DLNEWS' ? '0.05em' : '-0.01em',
                lineHeight: 1,
                textAlign: 'center',
                padding: '0 2px',
              }}
            >
              {p.word}
            </span>
          )}
        </motion.div>
      ))}
    </div>
  </div>
);

// ─── Tile 5 · Present to Audience — stage / spotlight visual ───
const StageVisual = () => (
  <div className="relative h-32 sm:h-36 rounded-xl bg-[rgba(255,255,255,0.02)] border border-white/6 overflow-hidden">
    {/* Spotlight cones */}
    <div className="absolute inset-x-0 top-0 h-full pointer-events-none">
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-[70%] h-full"
        style={{
          background: 'radial-gradient(ellipse 60% 100% at 50% 0%, rgba(218,255,1,0.18) 0%, transparent 60%)',
        }}
      />
    </div>
    {/* Stage floor */}
    <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-[rgba(218,255,1,0.08)] to-transparent" />
    {/* Presenter */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
      <motion.div
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        className="w-8 h-8 rounded-full"
        style={{
          background: 'linear-gradient(135deg, var(--accent-primary), rgba(218,255,1,0.6))',
          boxShadow: '0 0 24px rgba(218,255,1,0.5)',
        }}
      />
      <div className="w-16 h-2 mt-1 rounded-full bg-black/60 border border-white/8" />
    </div>
    {/* Slide behind the presenter */}
    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-14 rounded-md bg-[rgba(20,22,28,0.8)] border border-[var(--accent-primary)]/40 flex flex-col items-start p-1.5 gap-1">
      <div className="w-8 h-1 rounded-full bg-[var(--accent-primary)]" />
      <div className="w-16 h-0.5 rounded-full bg-white/20" />
      <div className="w-12 h-0.5 rounded-full bg-white/15" />
      <div className="w-14 h-0.5 rounded-full bg-white/15" />
    </div>
    {/* Audience dots */}
    <div className="absolute bottom-1.5 inset-x-0 flex justify-center gap-1">
      {Array.from({ length: 22 }).map((_, i) => (
        <motion.div
          key={i}
          className="w-1 h-1 rounded-full bg-white/40"
          animate={{ opacity: [0.3, 0.9, 0.3] }}
          transition={{ duration: 1.6, delay: i * 0.05, repeat: Infinity }}
        />
      ))}
    </div>
    {/* Live badge */}
    <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/30">
      <span className="w-1 h-1 rounded-full bg-red-400 animate-pulse" />
      <span className="text-[8px] font-bold text-red-300">ON STAGE</span>
    </div>
  </div>
);

// ─── Tile 6 · JVs / M&A / Partnerships — two orbs merging into one ───
const DealsVisual = () => (
  <div className="relative h-32 sm:h-36 rounded-xl bg-[rgba(255,255,255,0.02)] border border-white/6 overflow-hidden">
    {/* grid backdrop */}
    <svg viewBox="0 0 300 130" className="absolute inset-0 w-full h-full opacity-25">
      <defs>
        <pattern id="grid-jvs" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="300" height="130" fill="url(#grid-jvs)" />
    </svg>

    {/* Two glowing orbs converging in the middle */}
    <div className="absolute inset-0 flex items-center justify-center">
      {/* Left orb - blue */}
      <motion.div
        initial={{ x: -70 }}
        animate={{ x: [-70, -18, -70] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute w-14 h-14 sm:w-16 sm:h-16 rounded-full"
        style={{
          background: 'radial-gradient(circle at 40% 40%, #7dd3fc 0%, #38bdf8 45%, #0369a1 100%)',
          boxShadow: '0 0 30px 4px rgba(56,189,248,0.55), inset 0 0 12px rgba(255,255,255,0.4)',
        }}
      />

      {/* Right orb - accent yellow */}
      <motion.div
        initial={{ x: 70 }}
        animate={{ x: [70, 18, 70] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute w-14 h-14 sm:w-16 sm:h-16 rounded-full"
        style={{
          background: 'radial-gradient(circle at 40% 40%, #f7ffb0 0%, #daff01 45%, #7a8f00 100%)',
          boxShadow: '0 0 30px 4px rgba(218,255,1,0.55), inset 0 0 12px rgba(255,255,255,0.4)',
        }}
      />

      {/* Center collision spark — appears when orbs meet */}
      <motion.div
        animate={{ opacity: [0, 1, 1, 0], scale: [0.4, 1.3, 1.5, 0.4] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', times: [0, 0.4, 0.55, 0.75] }}
        className="relative w-3 h-3 rounded-full z-10"
        style={{
          background: '#FFFFFF',
          boxShadow: '0 0 24px 6px rgba(255,255,255,0.9), 0 0 40px 10px rgba(218,255,1,0.55)',
        }}
      />

      {/* radiating pulse rings from center — synced to collision */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border pointer-events-none"
          style={{ borderColor: 'rgba(218,255,1,0.4)' }}
          initial={{ width: 8, height: 8, opacity: 0 }}
          animate={{ width: [8, 100], height: [8, 100], opacity: [0, 0.6, 0] }}
          transition={{
            duration: 2.2,
            repeat: Infinity,
            delay: 1.7 + i * 0.35,
            repeatDelay: 2.3,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  </div>
);

// ─── Tile 7 · Post Event Report & Analytics — clean dashboard preview ───
const ReportVisual = () => (
  <div className="relative h-32 sm:h-36 rounded-xl bg-[rgba(255,255,255,0.02)] border border-white/6 overflow-hidden p-3">
    {/* card header */}
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1.5">
        <div className="w-4 h-4 rounded-sm bg-[var(--accent-primary)]/20 border border-[var(--accent-primary)]/50 flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-sm bg-[var(--accent-primary)]" />
        </div>
        <span className="text-[10px] font-bold text-white">Impact Report</span>
      </div>
      <span className="text-[8px] font-mono text-white/40 uppercase tracking-widest">PDF · LIVE</span>
    </div>

    {/* Chart area */}
    <div className="relative h-14 rounded-md bg-[rgba(255,255,255,0.02)] border border-white/6 px-2 py-1.5 overflow-hidden">
      {/* horizontal grid */}
      <div className="absolute inset-x-2 top-2 h-px bg-white/5" />
      <div className="absolute inset-x-2 top-1/2 h-px bg-white/5" />
      <div className="absolute inset-x-2 bottom-2 h-px bg-white/5" />

      <svg viewBox="0 0 260 44" preserveAspectRatio="none" className="absolute inset-0 w-full h-full p-1.5">
        <defs>
          <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          d="M 0 34 L 22 30 L 44 32 L 66 24 L 88 26 L 110 18 L 132 20 L 154 12 L 176 14 L 198 8 L 220 10 L 242 4 L 260 6 L 260 44 L 0 44 Z"
          fill="url(#area-grad)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9 }}
        />
        <motion.path
          d="M 0 34 L 22 30 L 44 32 L 66 24 L 88 26 L 110 18 L 132 20 L 154 12 L 176 14 L 198 8 L 220 10 L 242 4 L 260 6"
          fill="none"
          stroke="var(--accent-primary)"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.6, ease: 'easeInOut' }}
        />
        <motion.circle
          cx="260" cy="6" r="2.5" fill="var(--accent-primary)"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.4, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, delay: 1.5 }}
        />
      </svg>
    </div>

    {/* metric labels — no fake numbers */}
    <div className="grid grid-cols-4 gap-1 mt-2">
      {['Reach', 'Meetings', 'Follow-ups', 'Deals'].map((m, i) => (
        <motion.div
          key={m}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 + i * 0.08 }}
          className="text-[8px] sm:text-[9px] text-white/50 truncate text-center px-1 py-0.5 rounded bg-white/3 border border-white/6"
        >
          {m}
        </motion.div>
      ))}
    </div>
  </div>
);

const TILES = [
  {
    title: 'B2B Integrations',
    lead: 'Meet the wallets, bridges, oracles and chains ready to route through your protocol, with warm intros to the right decision-makers.',
    visual: IntegrationsVisual,
    span: 'md:col-span-2',
  },
  {
    title: 'Co-Marketing',
    lead: 'Book Spaces, AMAs and joint campaigns with protocols targeting your exact audience. Trade audiences, don’t buy them.',
    visual: CoMarketingVisual,
  },
  {
    title: 'Ecosystem Grants',
    lead: 'Direct intros to grant program leads across major ecosystems. Ask questions on the spot, skip the cold-form queue.',
    visual: GrantsVisual,
  },
  {
    title: 'Media & Distribution',
    lead: 'Get on the radar of the social platforms and crypto media outlets where your buyers actually spend their time.',
    visual: MediaVisual,
    span: 'md:col-span-2',
  },
  {
    title: 'Present to a Curated Audience',
    lead: 'Sponsors get to present their protocol on stage to a hand-picked, high-signal audience group. No spray-and-pray, just decision-makers in the room.',
    visual: StageVisual,
  },
  {
    title: 'JVs, M&A & Partnerships',
    lead: 'Explore joint ventures, mergers, acquisitions and deep partnerships with key players in the space, introduced and warmed up by our team.',
    visual: DealsVisual,
  },
  {
    title: 'Post-Event Report & Analytics',
    lead: 'A polished impact report lands in your inbox after the summit: reach, meetings booked, follow-ups triggered and deal signals, all measured, none guessed.',
    visual: ReportVisual,
  },
];

const OpportunitiesSection = () => {
  const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true });

  return (
    <section ref={ref} className="section-padding bg-[var(--bg-secondary)]" data-testid="opportunities-section">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <span className="text-xs sm:text-sm text-[var(--accent-primary)] uppercase tracking-widest font-medium">
            Unlock Opportunities
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mt-3 sm:mt-4 mb-3 sm:mb-4">
            What You&apos;ll Gain at
            <span className="text-[var(--accent-primary)]"> LayerSeek</span>
          </h2>
          <p className="text-sm md:text-base text-[var(--text-secondary)]">
            Real opportunities to grow your protocol · not just badges and business cards.
          </p>
        </motion.div>

        {/* Frosted-glass bento · asymmetric 3-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {TILES.map((t, i) => {
            const Visual = t.visual;
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative overflow-hidden rounded-2xl p-4 sm:p-5 border transition-all ${t.span || ''}`}
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  borderColor: 'rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                }}
              >
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--accent-primary)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="mb-4">
                  <Visual />
                </div>

                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-base sm:text-lg font-bold text-white leading-tight">{t.title}</h3>
                  <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[var(--accent-primary)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </div>
                <p className="text-xs sm:text-sm text-[var(--text-muted)] leading-relaxed">
                  {t.lead}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom banner — qualitative, no fabricated numbers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="mt-10 md:mt-14 p-6 md:p-8 rounded-2xl bg-gradient-to-r from-[var(--bg-tertiary)] to-[rgba(218,255,1,0.05)] border border-[var(--accent-primary)]"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-1 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--accent-primary)]" />
                Join from anywhere globally
              </h3>
              <p className="text-sm text-[var(--text-muted)]">No flights, hotels, visas or travel costs required.</p>
            </div>
            <div className="flex items-center gap-6 sm:gap-8">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-[var(--accent-primary)]">100%</div>
                <div className="text-xs text-[var(--text-muted)]">Virtual</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-[var(--accent-primary)]">B2B</div>
                <div className="text-xs text-[var(--text-muted)]">Focused</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-[var(--accent-primary)]">Curated</div>
                <div className="text-xs text-[var(--text-muted)]">Audience</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OpportunitiesSection;
